/**
 * OptionChartPicker — a compact toolbar control for the Charts page (single &
 * 2-split layouts) that lets the user chart any option contract by picking
 * Underlying → Expiry → Strike → Type, instead of hunting for the cryptic option
 * symbol (e.g. C-BTC-68000-200626) in the left symbol pane.
 *
 * It reuses the SAME backend the 4-quad layout uses:
 *   GET /charts/option-chain?symbol=<underlying>   → live expiries + strikes
 *   GET /charts/option-symbol?underlying&strike&expiry&type → resolved symbol
 * On "View chart" it calls onPick(symbol, exchange); the parent points the chart
 * at that symbol (works for both single and split, which share one symbol).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Popover, Typography, Select, MenuItem, TextField,
  ToggleButton, ToggleButtonGroup, IconButton, CircularProgress, Tooltip, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface Props {
  /** Current chart symbol — seeds the underlying field when the picker opens. */
  currentSymbol: string;
  /** Chart the resolved option symbol (parent calls its symbol-select handler). */
  onPick: (symbol: string, exchange: string) => void;
}

/**
 * Derive the UNDERLYING to seed the picker with, from whatever is charted. If the
 * charted symbol is itself an option contract we map it back to its underlying so
 * the chain loads (charting a BTC put would otherwise seed 'P-BTC-…' and fail):
 *   • Delta option  {C|P}-BTC-63000-200626  → BTCUSD
 *   • NSE/NFO option NIFTY2461324000CE / RELIANCE24JUN3000PE → leading letters
 * Anything else (BTCUSD, NIFTY, RELIANCE) is already an underlying — use as-is.
 */
function deriveUnderlying(sym: string): string {
  const s = (sym || '').toUpperCase().trim();
  if (!s) return '';
  const delta = /^[CP]-([A-Z]+)-\d+(?:\.\d+)?-\d{6}$/.exec(s);
  if (delta) return `${delta[1]}USD`;
  if (/(CE|PE)$/.test(s) && /\d/.test(s)) {
    const m = /^([A-Z&]+?)\d/.exec(s);
    if (m) return m[1];
  }
  return s;
}

/** ISO date (YYYY-MM-DD) → "20 Jun 26" for compact display. */
function fmtExpiry(iso: string): string {
  const [y, m, d] = (iso || '').split('-');
  if (!y || !m || !d) return iso;
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1] || m;
  return `${d} ${mon} ${y.slice(-2)}`;
}

const OptionChartPicker: React.FC<Props> = ({ currentSymbol, onPick }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const [und, setUnd] = useState('');
  const [expiries, setExpiries] = useState<string[]>([]);
  const [strikes, setStrikes] = useState<number[]>([]);
  const [resolvedUnd, setResolvedUnd] = useState(''); // backend's underlying id
  const [spot, setSpot] = useState(0);
  const [expiry, setExpiry] = useState('');
  const [strike, setStrike] = useState<number | null>(null);
  const [optType, setOptType] = useState<'CE' | 'PE'>('CE');
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);

  const loadRef = useRef(0);

  // Each time the popover opens, (re)seed the underlying from the currently
  // charted symbol — derived to an underlying if an option is charted — and reset
  // the chain so it reloads for that underlying. So it always follows the pane.
  useEffect(() => {
    if (!open) return;
    setUnd(deriveUnderlying(currentSymbol));
    setExpiries([]); setStrikes([]); setExpiry(''); setStrike(null); setSpot(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadChain = async (u: string) => {
    const sym = (u || '').trim().toUpperCase();
    if (!sym) return;
    const id = ++loadRef.current;
    setLoading(true);
    try {
      const resp = await api.get<any>('/charts/option-chain', { params: { symbol: sym } });
      if (loadRef.current !== id) return;
      const d = resp.data?.data || {};
      const exps: string[] = d.expiries || [];
      const strks: number[] = d.strikes || [];
      setExpiries(exps);
      setStrikes(strks);
      setResolvedUnd(d.underlying || sym);
      setSpot(Number(d.spotPrice) || 0);
      // Default expiry = nearest; strike = ATM (nearest to spot).
      setExpiry(prev => (prev && exps.includes(prev) ? prev : (exps[0] || '')));
      const sp = Number(d.spotPrice) || 0;
      const atm = sp && strks.length
        ? strks.reduce((p, c) => (Math.abs(c - sp) < Math.abs(p - sp) ? c : p))
        : strks[Math.floor(strks.length / 2)] ?? null;
      setStrike(prev => (prev != null && strks.includes(prev) ? prev : atm));
      if (!exps.length) toast.error(`No option contracts found for ${sym}`);
    } catch {
      if (loadRef.current !== id) return;
      setExpiries([]); setStrikes([]);
      toast.error(`Could not load option chain for ${sym}`);
    } finally {
      if (loadRef.current === id) setLoading(false);
    }
  };

  // Auto-load the chain when the popover opens with a seeded underlying.
  useEffect(() => {
    if (open && und && !expiries.length && !loading) loadChain(und);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, und]);

  // Strikes near the chosen strike / spot, so the dropdown isn't thousands long.
  const shownStrikes = useMemo(() => {
    const ref = strike ?? spot;
    if (!ref || !strikes.length) return strikes.slice(0, 60);
    return strikes.filter(s => Math.abs(s - ref) <= ref * 0.12);
  }, [strikes, strike, spot]);

  const view = async () => {
    if (!resolvedUnd || !expiry || strike == null) { toast.error('Pick underlying, expiry & strike'); return; }
    setResolving(true);
    try {
      const resp = await api.get<any>('/charts/option-symbol', {
        params: { underlying: resolvedUnd, strike, expiry, type: optType },
      });
      const d = resp.data?.data;
      if (d?.symbol) {
        onPick(d.symbol, d.exchange || 'DELTA');
        setAnchorEl(null);
      } else {
        toast.error('Could not resolve that option contract');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Option contract not found');
    } finally {
      setResolving(false);
    }
  };

  const selectSx = { '& .MuiSelect-select': { py: 0.4, fontSize: '0.75rem' } };

  return (
    <>
      <Tooltip title="Chart an option by Underlying / Expiry / Strike (no need to search the symbol)">
        <Button size="small" variant="outlined" startIcon={<CandlestickChartIcon sx={{ fontSize: 15 }} />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ py: 0.15, px: 0.7, minWidth: 0, fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap', '& .MuiButton-startIcon': { mr: 0.4 } }}>
          OC
        </Button>
      </Tooltip>
      <Popover open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Box sx={{ p: 1.5, width: 290 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', mb: 0.8 }}>
            Chart an option contract
          </Typography>

          {/* Underlying */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 1 }}>
            <TextField size="small" fullWidth label="Underlying" value={und}
              onChange={(e) => setUnd(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') loadChain(und); }}
              InputProps={{ sx: { fontSize: '0.78rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.78rem' } }} />
            <Tooltip title="Load contracts from broker">
              <span>
                <IconButton size="small" disabled={loading || !und.trim()} onClick={() => loadChain(und)}>
                  {loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {spot > 0 && (
            <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary', mb: 0.6 }}>
              Spot ≈ {spot.toLocaleString()} · {strikes.length} strikes · {expiries.length} expiries
            </Typography>
          )}

          {/* Type */}
          <ToggleButtonGroup size="small" exclusive value={optType} fullWidth
            onChange={(_e, v) => { if (v) setOptType(v); }} sx={{ mb: 1 }}>
            <ToggleButton value="CE" sx={{ py: 0.2, fontSize: '0.72rem', fontWeight: 700 }}>CALL (CE)</ToggleButton>
            <ToggleButton value="PE" sx={{ py: 0.2, fontSize: '0.72rem', fontWeight: 700 }}>PUT (PE)</ToggleButton>
          </ToggleButtonGroup>

          {/* Expiry */}
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', mb: 0.2 }}>Expiry</Typography>
          <Select size="small" fullWidth value={expiry} displayEmpty sx={{ ...selectSx, mb: 1 }}
            onChange={(e) => setExpiry(e.target.value as string)}>
            {!expiries.length && <MenuItem value="" disabled>—</MenuItem>}
            {expiries.map(exp => <MenuItem key={exp} value={exp} sx={{ fontSize: '0.75rem' }}>{fmtExpiry(exp)}</MenuItem>)}
          </Select>

          {/* Strike */}
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', mb: 0.2 }}>Strike</Typography>
          <Select size="small" fullWidth value={strike ?? ''} displayEmpty sx={{ ...selectSx, mb: 1.2 }}
            onChange={(e) => setStrike(Number(e.target.value))}>
            {!shownStrikes.length && <MenuItem value="" disabled>—</MenuItem>}
            {shownStrikes.map(s => (
              <MenuItem key={s} value={s} sx={{ fontSize: '0.75rem' }}>
                {s.toLocaleString()}{spot > 0 && Math.abs(s - spot) < 1e-9 ? ' (ATM)' : ''}
              </MenuItem>
            ))}
          </Select>

          <Divider sx={{ mb: 1 }} />
          <Button fullWidth size="small" variant="contained" disabled={resolving || !expiry || strike == null}
            onClick={view} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {resolving ? <CircularProgress size={16} /> : 'View chart'}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default OptionChartPicker;
